// src/pages/QuestionnairePage.jsx
/* ------------------------------------------------------------------ */
/* QuestionnairePage – dynamic assessment hub                         */
/* ------------------------------------------------------------------ */
/**
 * Routes handled
 * --------------
 * • `/questionnaire`                 → `<QuestionnaireChooser />`
 * • `/questionnaire/:questionnaire`  → `<DynamicQuestionnaireForm />`
 *
 * Design notes
 * ------------
 * 1. The “chooser” UI was split into its own component for clarity and reuse.
 * 2. The list of available assessments lives in a single **`QUESTIONNAIRES`**
 *    array so adding / removing a questionnaire is a single-line diff and
 *    easily unit-testable.
 *
 * Future work
 * -----------
 * • Move `QUESTIONNAIRES` to `utils/questionnaireRegistry.js` if other
 *   surfaces (e.g. dashboard, side-menu) also need the list.
 * • Promote `<QuestionnaireChooser/>` into `components/questionnaire/`
 *   if a mobile layout or other page needs to re-use it.
 *
 * @component
 * @returns {JSX.Element}
 */

import React, { memo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useParams } from "react-router-dom";
import DynamicQuestionnaireForm from "../components/questionnaire/DynamicQuestionnaireForm";
import QuestionnaireHistoryList from "../components/questionnaire/QuestionnaireHistoryList";
import { QuestionnaireChooser } from "../components/questionnaire/QuestionnaireChooser";
import { trackEvent } from "../utils/analytics/track";
import RegionalSupportCodeNotice from "../components/backend/RegionalSupportCodeNotice";
import QuestionnairePageLoadingShell from "../components/pageLoading/QuestionnairePageLoadingShell";
import { getRegionalSupportCode } from "../utils/backend/regionalSupportCodes";

/* ------------------------------------------------------------------ */
/* Main component                                                     */
/* ------------------------------------------------------------------ */
function QuestionnairePage({
  uid = null,
  permittedQuestionnaires = [],
  isQuestionnairePermitted = () => false,
  loadingQuestionnaires = false,
  recordingAccessError = null,
  historyRefreshKey = 0,
  onQuestionnaireSubmitted = () => {},
}) {
  const { t } = useTranslation();
  const { questionnaireName } = useParams();
  const lastTrackedRef = useRef(null);

  /* ---------- No slug → show list ----------------------------- */
  useEffect(() => {
    if (loadingQuestionnaires) return;
    if (!permittedQuestionnaires.length) return;
    if (questionnaireName && !isQuestionnairePermitted(questionnaireName)) {
      return;
    }

    // Track chooser view when no questionnaire is selected
    if (!questionnaireName) {
      if (lastTrackedRef.current !== "chooser") {
        trackEvent("questionnaires_view");
        lastTrackedRef.current = "chooser";
      }
      return;
    }

    // Track questionnaire open when a questionnaire is selected
    const key = `q:${questionnaireName}`;
    if (lastTrackedRef.current !== key) {
      trackEvent("questionnaire_open", { questionnaire: questionnaireName });
      lastTrackedRef.current = key;
    }
  }, [
    questionnaireName,
    loadingQuestionnaires,
    permittedQuestionnaires,
    isQuestionnairePermitted,
  ]);

  if (loadingQuestionnaires) {
    return <QuestionnairePageLoadingShell />;
  }

  if (recordingAccessError) {
    return (
      <div className="w-full max-w-lg p-4">
        <section className="rounded-2xl border border-greyscale-6 bg-greyscale-8/40 p-4 text-left">
          <p className="subtitle-text mb-2">
            {t("regionalBackend.questionnaireAccess.title")}
          </p>
          <p className="muted-text">
            {recordingAccessError === "fetch_failed"
              ? t("regionalBackend.questionnaireAccess.fetchFailed")
              : t("regionalBackend.questionnaireAccess.notFound")}
          </p>
          <RegionalSupportCodeNotice
            supportCode={getRegionalSupportCode(
              recordingAccessError === "fetch_failed"
                ? "QUESTIONNAIRE_ACCESS_FETCH_FAILED"
                : "QUESTIONNAIRE_ACCESS_NOT_FOUND",
            )}
          />
        </section>
      </div>
    );
  }

  if (!permittedQuestionnaires.length) {
    return <Navigate to="/home" replace />;
  }

  if (questionnaireName && !isQuestionnairePermitted(questionnaireName)) {
    return <Navigate to="/questionnaires" replace />;
  }

  if (!questionnaireName) {
    return (
      <div className="w-full max-w-lg min-h-full flex flex-col items-center gap-4">
        <div className="w-full px-4 md:px-0">
          <QuestionnaireChooser questionnaireSlugs={permittedQuestionnaires} />
        </div>
        <QuestionnaireHistoryList uid={uid} refreshKey={historyRefreshKey} />
      </div>
    );
  }

  return (
    <div className="form-page w-full h-full max-w-lg p-4 flex flex-col gap-4">
      <DynamicQuestionnaireForm onSubmitted={onQuestionnaireSubmitted} />
    </div>
  );
}

export default memo(QuestionnairePage);
